import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Upload,
  FileText,
  Lock,
  Plus,
} from 'lucide-react'
import { portfolioAPI, tradesAPI, externalHoldingsAPI, mfAPI, portfolioSnapshotAPI, streamingAPI, isApiPaused } from '../services/api'
import { useWebSocket } from '../contexts/WebSocketContext'
import { useThrottledTicks } from '../hooks/useThrottledTicks'
import LoadingSpinner from '../components/LoadingSpinner'
import OrderModal from '../components/OrderModal'
import AutomationPanel from '../components/AutomationPanel'
import SortableTable from '../components/SortableTable'
import HoldingsTable from '../components/HoldingsTable'
import JsonUploadPanel from '../components/JsonUploadPanel'
import PortfolioSummaryBar from '../components/PortfolioSummaryBar'
import PasswordConfirmModal from '../components/PasswordConfirmModal'
import HoldingFormModal from '../components/HoldingFormModal'
import MFTab from '../components/MFTab'
import { useAuth } from '../contexts/AuthContext'
import { openTradingView } from '../utils/tradingView'
import toast from 'react-hot-toast'

const HOLDINGS_TABS = [
  { id: 'kite_pm', label: 'KitePM', live: true },
  { id: 'kite_rj', label: 'Kite RJ', live: false },
  { id: 'angelone_rj', label: 'AngelOne RJ', live: false },
  { id: 'angelone_pm', label: 'AngelOne PM', live: false },
  { id: 'mf', label: 'Mutual Funds', live: false },
]

const ALL_ACCOUNTS = ['kite_pm', 'kite_pm_live', 'kite_rj', 'angelone_rj', 'angelone_pm', 'mf']

const Portfolio = () => {
  const { user } = useAuth()
  const { symbolTicks } = useWebSocket()          // real-time — used only for flushLTPsToDb
  const displayTicks = useThrottledTicks(5000)    // throttled 5 s — used for all table/display
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [holdingsTab, setHoldingsTab] = useState('kite_pm')
  const [orderModal, setOrderModal] = useState({ isOpen: false, stock: null, orderType: null, ltp: 0 })
  const [holdingFormModal, setHoldingFormModal] = useState({
    isOpen: false,
    editIndex: null,
    initialData: null,
    accountName: null,
  })

  // Automation password gate
  const [passwordModal, setPasswordModal] = useState(false)
  const [automationUnlocked, setAutomationUnlocked] = useState(false)

  // Live Kite data
  const [data, setData] = useState({ holdings: [], positions: [], margins: null })

  // External account holdings keyed by accountName (includes kite_pm supplemental)
  const [externalHoldings, setExternalHoldings] = useState(
    Object.fromEntries(ALL_ACCOUNTS.map((a) => [a, null]))
  )

  // MF summary reported back by MFTab component (active value + sold P&L for overall accuracy)
  const [mfSummary, setMfSummary] = useState({
    totalValue: 0, totalPnl: 0, totalPnlPct: 0,
    activeInvested: 0, soldPnl: 0, soldInvested: 0,
  })

  // STCG/LTCG holding ages from tradebook FIFO — { SYMBOL: { daysHeld, isLTCG } }
  const [holdingAges, setHoldingAges] = useState({})

  // Snapshot tracking: fires once after all data loads
  const [externalLoaded, setExternalLoaded] = useState(false)
  const [mfLoaded, setMfLoaded] = useState(false)
  const [snapshotSaved, setSnapshotSaved] = useState(false)

  // CSV / trade history
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadedTrades, setUploadedTrades] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadSummary, setUploadSummary] = useState(null)
  const [tradeView, setTradeView] = useState('ACTIVE')
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)
  const [autoRefreshSec, setAutoRefreshSec] = useState(60)

  // ── Initialise ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (user?.kiteConnected) {
      fetchPortfolioData()
      fetchUploadedTrades()
    } else {
      setLoading(false)
    }
    fetchExternalHoldings()
    fetchMFSummary()   // populate MF summary bar without waiting for MF tab to open
    fetchHoldingAges() // STCG/LTCG badges for equity holdings
  }, [user?.kiteConnected])

  useEffect(() => {
    try {
      const en = localStorage.getItem('portfolio_auto_refresh_enabled')
      const sec = localStorage.getItem('portfolio_auto_refresh_sec')
      if (en !== null) setAutoRefreshEnabled(en === 'true')
      if (sec !== null) setAutoRefreshSec(Math.max(5, parseInt(sec, 10) || 60))
    } catch {}
  }, [])

  useEffect(() => {
    if (!user?.kiteConnected || !autoRefreshEnabled) return
    const ms = Math.max(30, Number(autoRefreshSec) || 60) * 1000  // floor at 30 s
    const id = setInterval(() => { if (!isApiPaused()) handleRefresh() }, ms)
    return () => clearInterval(id)
  }, [user?.kiteConnected, autoRefreshEnabled, autoRefreshSec])

  // ── Flush WS LTPs → ExternalHoldings DB ────────────────────────────────────
  // When streaming is active, write back live ticks to ExternalHoldings so
  // backend services (Health, health analysis) always read current prices.
  // Called: (a) once at startup after first ticks arrive, (b) every 2 hours.
  //
  // kiteLiveHoldingsRef holds the latest kiteLiveHoldings without putting it
  // in useCallback deps (which would cause a TDZ crash — the variable is
  // declared later in the render body, after the useCallback call).
  const lastFlushRef        = useRef(0)
  const kiteLiveHoldingsRef = useRef([])

  const flushLTPsToDb = useCallback(async () => {
    if (!symbolTicks || Object.keys(symbolTicks).length === 0) return
    const now = Date.now()
    if (now - lastFlushRef.current < 60_000) return  // debounce: min 60s between flushes
    lastFlushRef.current = now

    // External equity accounts (not kite_pm — that's handled by fetchPortfolioData)
    const EXT = ['kite_rj', 'angelone_rj', 'angelone_pm']
    for (const accountName of EXT) {
      const stored = externalHoldings[accountName]?.normalizedHoldings
      if (!stored?.length) continue
      let anyUpdate = false
      const updated = stored.map(h => {
        const tick = symbolTicks[h.symbol]
        if (!tick?.ltp || tick.ltp <= 0) return h
        const qty          = (h.quantity || 0) + (h.t1Quantity || 0)
        const currentValue = tick.ltp * qty
        const invested     = (h.averagePrice || 0) * qty
        const pnl          = invested > 0 ? currentValue - invested : (h.pnl || 0)
        const pnlPct       = invested > 0 ? (pnl / invested) * 100 : 0
        anyUpdate = true
        return { ...h, ltp: tick.ltp, currentValue, pnl, pnlPercentage: parseFloat(pnlPct.toFixed(2)) }
      })
      if (anyUpdate) {
        externalHoldingsAPI.post(accountName, { holdings: updated, normalized: true }).catch(() => {})
      }
    }

    // kite_pm_live: save live Kite holdings with WS-overridden LTPs.
    // Use the ref (updated every render after kiteLiveHoldings is computed) to
    // avoid a stale closure — kiteLiveHoldings cannot be in the deps array
    // because it is declared after this useCallback in the component body.
    const liveHoldings = kiteLiveHoldingsRef.current
    if (liveHoldings.length > 0) {
      const kiteUpdated = liveHoldings.map(h => ({
        symbol:       h.symbol,       exchange:  h.exchange || 'NSE',
        isin:         h.isin || '',   quantity:  h.quantity || 0,
        t1Quantity:   h.t1Quantity || 0,
        averagePrice: h.averagePrice || 0,
        ltp:          h.ltp,          currentValue: h.currentValue,
        pnl:          h.pnl,          pnlPercentage: h.pnlPercentage || 0,
        dayChange:    h.dayChange || 0, dayChangePct: h.dayChangePct || 0,
      }))
      externalHoldingsAPI.post('kite_pm_live', { holdings: kiteUpdated, normalized: true }).catch(() => {})
    }
  }, [symbolTicks, externalHoldings])  // kiteLiveHoldings intentionally omitted — use ref instead

  // Fire flush once after ticks arrive (delayed 8s to let stream stabilise)
  // and then every 2 hours while the page is open.
  const flushScheduledRef = useRef(false)
  useEffect(() => {
    const tickCount = Object.keys(symbolTicks || {}).length
    if (tickCount === 0) return
    // First flush: 8 seconds after initial ticks arrive (once per session)
    if (!flushScheduledRef.current) {
      flushScheduledRef.current = true
      const t = setTimeout(() => flushLTPsToDb(), 8_000)
      return () => clearTimeout(t)
    }
  }, [symbolTicks, flushLTPsToDb])  // eslint-disable-line

  useEffect(() => {
    // 2-hour recurring flush — keeps DB prices current
    const TWO_HOURS = 2 * 60 * 60 * 1000
    const id = setInterval(() => { if (!isApiPaused()) flushLTPsToDb() }, TWO_HOURS)
    return () => clearInterval(id)
  }, [flushLTPsToDb])

  // ── Subscribe ALL portfolio symbols to the running stream ───────────────────
  // Runs whenever holdings change. Best-effort: if streaming isn't running the
  // backend subscribeSymbols() is a no-op; if it IS running the symbols are
  // added immediately so LTP ticks start arriving for Portfolio holdings.
  useEffect(() => {
    const symSet = new Set()
    const clean  = (s) => (s || '').toUpperCase().replace(/-EQ$/i, '').trim()

    // Live Kite holdings
    data.holdings.forEach((h) => { const s = clean(h.tradingsymbol); if (s) symSet.add(s) })

    // All external account snapshots
    const EXT = ['kite_pm_live', 'kite_rj', 'angelone_rj', 'angelone_pm']
    EXT.forEach((acc) => {
      (externalHoldings[acc]?.normalizedHoldings || []).forEach((h) => {
        const s = clean(h.symbol); if (s) symSet.add(s)
      })
    })

    if (symSet.size > 0) {
      streamingAPI.subscribe([...symSet]).catch(() => {})
    }
  }, [data.holdings, externalHoldings])   // eslint-disable-line

  // ── Portfolio snapshot — fires once when all data sources have loaded ──────
  useEffect(() => {
    if (snapshotSaved) return
    if (loading && user?.kiteConnected) return // wait for Kite data
    if (!externalLoaded) return
    if (!mfLoaded) return

    const liveSummary = (name) => computeExtLiveSummary(name)
    const accounts = [
      {
        accountName: 'kite_pm',
        invested:     kitePMSummary.totalValue - kitePMSummary.totalPnl,
        currentValue: kitePMSummary.totalValue,
      },
      {
        accountName: 'kite_rj',
        invested:     (liveSummary('kite_rj').totalValue || 0) - (liveSummary('kite_rj').totalPnl || 0),
        currentValue: liveSummary('kite_rj').totalValue || 0,
      },
      {
        accountName: 'angelone_rj',
        invested:     (liveSummary('angelone_rj').totalValue || 0) - (liveSummary('angelone_rj').totalPnl || 0),
        currentValue: liveSummary('angelone_rj').totalValue || 0,
      },
      {
        accountName: 'angelone_pm',
        invested:     (liveSummary('angelone_pm').totalValue || 0) - (liveSummary('angelone_pm').totalPnl || 0),
        currentValue: liveSummary('angelone_pm').totalValue || 0,
      },
      {
        accountName: 'mf',
        invested:     mfSummary.activeInvested || 0,
        currentValue: mfSummary.totalValue || 0,
      },
    ]

    // Only save if at least one account has non-zero data
    if (accounts.some((a) => a.currentValue > 0)) {
      setSnapshotSaved(true)
      portfolioSnapshotAPI.save(accounts).catch((err) =>
        console.error('Portfolio snapshot save failed:', err)
      )
    }
  }, [loading, externalLoaded, mfLoaded, snapshotSaved])
  // ── ────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    try {
      localStorage.setItem('portfolio_auto_refresh_enabled', String(autoRefreshEnabled))
      localStorage.setItem('portfolio_auto_refresh_sec', String(autoRefreshSec))
    } catch {}
  }, [autoRefreshEnabled, autoRefreshSec])

  // ── Data fetchers ───────────────────────────────────────────────────────────
  const fetchPortfolioData = async () => {
    try {
      const [holdingsRes, positionsRes, marginsRes] = await Promise.allSettled([
        portfolioAPI.getHoldings(),
        portfolioAPI.getPositions(),
        portfolioAPI.getMargins(),
      ])
      const holdings = holdingsRes.status === 'fulfilled' ? holdingsRes.value.data?.data || [] : []
      setData({
        holdings,
        positions: positionsRes.status === 'fulfilled' ? positionsRes.value.data?.data?.net || [] : [],
        margins: marginsRes.status === 'fulfilled'
          ? marginsRes.value.data?.data || marginsRes.value.data || null
          : null,
      })
      // Auto-persist live Kite holdings so they're available when Kite is offline next time
      if (holdings.length) {
        externalHoldingsAPI.post('kite_pm_live', { holdings }).catch(() => {})
      }
    } catch (err) {
      console.error('Portfolio fetch error:', err)
      toast.error('Failed to fetch portfolio data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const fetchExternalHoldings = async () => {
    const results = await Promise.allSettled(ALL_ACCOUNTS.map((a) => externalHoldingsAPI.get(a)))
    const updated = {}
    ALL_ACCOUNTS.forEach((a, i) => {
      updated[a] = results[i].status === 'fulfilled' ? results[i].value.data : null
    })
    setExternalHoldings(updated)
    setExternalLoaded(true)
  }

  const fetchHoldingAges = async () => {
    try {
      const res = await tradesAPI.getHoldingAges()
      setHoldingAges(res.data.holdingAges || {})
    } catch {
      // Non-critical — tradebook may not be uploaded
    }
  }

  // Fetch MF summary eagerly so the summary bar populates without visiting the MF tab
  const fetchMFSummary = async () => {
    try {
      const txRes = await mfAPI.getTransactions()
      const txs = txRes.data.transactions || []
      if (!txs.length) return

      const active = txs.filter((t) => t.status === 'active')
      const sold   = txs.filter((t) => t.status === 'sold')

      // Fetch live NAVs for active ISINs (backend caches 30 min — fast on repeated calls)
      const activeIsins = [...new Set(active.filter((t) => t.isin).map((t) => t.isin))]
      let navs = {}
      if (activeIsins.length) {
        try {
          const navRes = await mfAPI.getNav(activeIsins)
          navs = navRes.data.navs || {}
        } catch {}
      }

      const activeInvested = active.reduce((s, t) => s + t.amount, 0)
      const activeValue = active.reduce((s, t) => {
        const buyNav = t.units > 0 ? t.amount / t.units : 0
        return s + t.units * (navs[t.isin] || buyNav)
      }, 0)
      const activePnl = activeValue - activeInvested

      const soldInvested = sold.reduce((s, t) => s + t.amount, 0)
      const soldValue    = sold.reduce((s, t) => s + t.units * (t.soldNav || (t.units > 0 ? t.amount / t.units : 0)), 0)
      const soldPnl      = soldValue - soldInvested

      setMfSummary({
        totalValue:     activeValue,
        totalPnl:       activePnl,
        totalPnlPct:    activeInvested > 0 ? (activePnl / activeInvested) * 100 : 0,
        activeInvested,
        soldPnl,
        soldInvested,
      })
    } catch (err) {
      console.error('MF summary fetch error:', err)
    } finally {
      setMfLoaded(true)
    }
  }

  const fetchUploadedTrades = async () => {
    try {
      const res = await tradesAPI.getTradeHistory({ limit: 500, page: 1 })
      setUploadedTrades(res.data.trades || [])
    } catch (err) {
      console.error('Failed to fetch trade history:', err)
    }
  }

  const handleRefresh = () => {
    setRefreshing(true)
    fetchPortfolioData()
    fetchUploadedTrades()
    fetchExternalHoldings()
    fetchMFSummary()
    fetchHoldingAges()
  }

  // ── Order modal ─────────────────────────────────────────────────────────────
  const openOrderModal = (stock, orderType, ltp = 0) => {
    setOrderModal({ isOpen: true, stock: { ...stock, exchange: stock.exchange || 'NSE' }, orderType, ltp: ltp || stock.last_price || 0 })
  }
  const closeOrderModal = () => {
    setOrderModal({ isOpen: false, stock: null, orderType: null, ltp: 0 })
    fetchPortfolioData()
  }

  // ── Holding CRUD ────────────────────────────────────────────────────────────
  const openAddHolding = (accountName) => {
    setHoldingFormModal({ isOpen: true, editIndex: null, initialData: null, accountName })
  }

  const openEditHolding = (accountName, holding, index) => {
    setHoldingFormModal({ isOpen: true, editIndex: index, initialData: holding, accountName })
  }

  const saveHolding = async (holding) => {
    const { accountName, editIndex } = holdingFormModal
    const current = externalHoldings[accountName]?.normalizedHoldings || []
    const updated =
      editIndex !== null && editIndex >= 0
        ? current.map((h, i) => (i === editIndex ? holding : h))
        : [...current, holding]
    try {
      const res = await externalHoldingsAPI.uploadNormalized(accountName, updated)
      setExternalHoldings((prev) => ({ ...prev, [accountName]: res.data }))
      setHoldingFormModal({ isOpen: false, editIndex: null, initialData: null, accountName: null })
      toast.success(editIndex !== null ? 'Holding updated' : 'Holding added')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save holding')
    }
  }

  const deleteHolding = async (accountName, index) => {
    const current = externalHoldings[accountName]?.normalizedHoldings || []
    const updated = current.filter((_, i) => i !== index)
    try {
      const res = await externalHoldingsAPI.uploadNormalized(accountName, updated)
      setExternalHoldings((prev) => ({ ...prev, [accountName]: res.data }))
      toast.success('Holding removed')
    } catch (err) {
      toast.error('Failed to remove holding')
    }
  }

  // ── Computed holdings ───────────────────────────────────────────────────────
  // Live Kite API holdings — LTP overridden by streaming when available (fallback to Kite API)
  const kiteLiveHoldings = data.holdings.map((h) => {
    const streamTick  = displayTicks?.[h.tradingsymbol]
    const ltp         = streamTick?.ltp ?? h.last_price ?? 0
    const qty         = (h.quantity || 0) + (h.t1_quantity || 0)
    // Guard: if ltp is 0 (no stream + no price from API), fall back to cost basis so
    // value is never ₹0 and doesn't create a fake massive loss.
    const currentValue = ltp > 0
      ? ltp * qty
      : (h.last_price > 0 ? h.last_price * qty : (h.average_price || 0) * qty)
    const invested    = (h.average_price || 0) * qty
    const pnl         = invested > 0 ? currentValue - invested : (h.pnl || 0)
    const pnlPct      = invested > 0 ? (pnl / invested) * 100 : 0
    return {
      symbol: h.tradingsymbol,
      exchange: h.exchange || 'NSE',
      isin: h.isin || '',
      quantity: h.quantity || 0,
      t1Quantity: h.t1_quantity || 0,
      averagePrice: h.average_price || 0,
      ltp,
      currentValue,
      pnl,
      pnlPercentage: pnlPct,
      dayChange: streamTick?.change ?? h.day_change ?? 0,
      dayChangePct: h.day_change_percentage || 0,
      _raw: h,
      _source: 'live',
      _editable: false,
      _streamLive: !!streamTick,
    }
  })
  // Keep ref in sync so flushLTPsToDb always has the latest live holdings
  // (the ref is defined before this variable, which is why we use a ref rather
  // than putting kiteLiveHoldings directly in the useCallback deps array)
  kiteLiveHoldingsRef.current = kiteLiveHoldings

  // When Kite is offline, fall back to the last-synced snapshot saved in DB.
  // WS ticks still override LTP if the AngelOne streamer is running.
  const kiteDbFallback = kiteLiveHoldings.length === 0
    ? (externalHoldings.kite_pm_live?.normalizedHoldings || []).map((h) => {
        const streamTick = displayTicks?.[h.symbol]
        const ltp        = streamTick?.ltp ?? h.ltp ?? 0
        const qty        = h.quantity || 0
        // If no LTP at all, use stored currentValue from DB snapshot, then cost basis as last resort
        const currentValue = ltp > 0
          ? ltp * qty
          : (h.currentValue > 0 ? h.currentValue : (h.averagePrice || 0) * qty)
        const invested   = (h.averagePrice || 0) * qty
        const pnl        = invested > 0 ? currentValue - invested : (h.pnl || 0)
        const pnlPct     = invested > 0 ? (pnl / invested) * 100 : 0
        return {
          ...h,
          ltp,
          currentValue,
          pnl,
          pnlPercentage: pnlPct,
          _source: 'db',
          _editable: false,
          _streamLive: !!streamTick,
        }
      })
    : []

  // KitePM supplemental (manually added) — marked editable with their own index
  const kitePMSupplemental = (externalHoldings.kite_pm?.normalizedHoldings || []).map((h, i) => ({
    ...h,
    _source: 'manual',
    _editable: true,
    _manualIndex: i,
  }))

  // Combined KitePM: live rows first (or DB fallback when offline), then manual
  const kitePMAllHoldings = [...kiteLiveHoldings, ...kiteDbFallback, ...kitePMSupplemental]

  // ── Live summary helper — recomputes from displayTicks (throttled 5s) ─────────
  // Uses WS LTP if available, falls back to snapshot LTP. Updates the summary
  // bar every 5 s to match table data (prevents flickering on fast ticks).
  const computeExtLiveSummary = (accountName) => {
    const holdings = externalHoldings[accountName]?.normalizedHoldings || []
    if (!holdings.length) return { totalValue: 0, totalPnl: 0, totalPnlPct: 0, holdingsCount: 0 }
    let totalValue = 0, totalPnl = 0, totalInvested = 0
    for (const h of holdings) {
      const qty = (h.quantity || 0) + (h.t1Quantity || 0)
      const ltp = displayTicks?.[h.symbol]?.ltp ?? h.ltp ?? 0
      // Never let currentValue be 0 when a holding exists — use stored value, then cost basis
      const currentValue = ltp > 0
        ? ltp * qty
        : (h.currentValue > 0 ? h.currentValue : (h.averagePrice || 0) * qty)
      const invested = (h.averagePrice || 0) * qty
      const pnl = invested > 0 ? currentValue - invested : (h.pnl || 0)
      totalValue   += currentValue
      totalPnl     += pnl
      totalInvested += invested
    }
    const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0
    return { totalValue, totalPnl, totalPnlPct, holdingsCount: holdings.length }
  }

  // ── Summary data ────────────────────────────────────────────────────────────
  const kitePMSummary = {
    totalValue: kitePMAllHoldings.reduce((s, h) => s + (h.currentValue || 0), 0),
    totalPnl: kitePMAllHoldings.reduce((s, h) => s + (h.pnl || 0), 0),
    holdingsCount: kitePMAllHoldings.length,
    totalPnlPct: 0,
  }
  const kitePMInvested = kitePMSummary.totalValue - kitePMSummary.totalPnl
  kitePMSummary.totalPnlPct = kitePMInvested > 0
    ? (kitePMSummary.totalPnl / kitePMInvested) * 100
    : 0

  const summaryAccounts = [
    { accountName: 'kite_pm',     label: 'KitePM',       summary: kitePMSummary },
    { accountName: 'kite_rj',     label: 'Kite RJ',      summary: computeExtLiveSummary('kite_rj') },
    { accountName: 'angelone_rj', label: 'AngelOne RJ',  summary: computeExtLiveSummary('angelone_rj') },
    { accountName: 'angelone_pm', label: 'AngelOne PM',  summary: computeExtLiveSummary('angelone_pm') },
  ]

  // Unique symbol count across all equity accounts (deduped — same stock in 2 accounts = 1 unique)
  const uniqueEquitySymbols = new Set([
    ...kitePMAllHoldings.map(h => h.symbol).filter(Boolean),
    ...(externalHoldings.kite_rj?.normalizedHoldings || []).map(h => h.symbol).filter(Boolean),
    ...(externalHoldings.angelone_rj?.normalizedHoldings || []).map(h => h.symbol).filter(Boolean),
    ...(externalHoldings.angelone_pm?.normalizedHoldings || []).map(h => h.symbol).filter(Boolean),
  ])
  const uniqueEquityCount = uniqueEquitySymbols.size

  // Active-tab summary (drives the two quick-stat cards)
  const activeTabDef = HOLDINGS_TABS.find((t) => t.id === holdingsTab)
  const activeTabSummary = (() => {
    if (holdingsTab === 'kite_pm') return { ...kitePMSummary, label: 'KitePM' }
    if (holdingsTab === 'mf') return {
      label: 'Mutual Funds',
      totalValue: mfSummary.totalValue || 0,
      totalPnl: mfSummary.totalPnl || 0,
      totalPnlPct: mfSummary.totalPnlPct || 0,
      holdingsCount: 0,
    }
    // External accounts: use live summary computed from symbolTicks
    const live = computeExtLiveSummary(holdingsTab)
    return {
      label: activeTabDef?.label || holdingsTab,
      totalValue:    live.totalValue,
      totalPnl:      live.totalPnl,
      totalPnlPct:   live.totalPnlPct,
      holdingsCount: live.holdingsCount,
    }
  })()

  // ── CSV upload ──────────────────────────────────────────────────────────────
  const handleUploadCsv = async () => {
    if (!selectedFile) { toast.error('Select a CSV file first'); return }
    setUploading(true)
    setUploadSummary(null)
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const res = await tradesAPI.uploadCsv(e.target.result)
        setUploadSummary(res.data)
        toast.success('CSV uploaded successfully!')
        fetchUploadedTrades()
        setSelectedFile(null)
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to upload CSV')
      } finally {
        setUploading(false)
      }
    }
    reader.readAsText(selectedFile)
  }

  // ── Trade grouping ──────────────────────────────────────────────────────────
  const getTradeDateTime = (trade) => {
    try {
      const d = new Date(trade.tradeDate)
      if (trade.tradeTime) {
        const [hh = '0', mm = '0', ss = '0'] = String(trade.tradeTime).split(':')
        d.setHours(+hh || 0, +mm || 0, +ss || 0, 0)
      }
      return d
    } catch { return new Date(trade.tradeDate) }
  }

  const groupedTrades = React.useMemo(() => {
    if (!uploadedTrades.length) return { active: [], squared: [] }
    const bySymbol = {}
    for (const t of uploadedTrades) {
      const key = t.tradingsymbol || t.symbol || t.tradingSymbol
      if (!key) continue
      if (!bySymbol[key]) bySymbol[key] = []
      bySymbol[key].push(t)
    }
    const active = [], squared = []
    Object.entries(bySymbol).forEach(([symbol, trades]) => {
      const sorted = [...trades].sort((a, b) => getTradeDateTime(a) - getTradeDateTime(b))
      let netQty = 0, curBuyQty = 0, curSellQty = 0, curBuyAmt = 0, curSellAmt = 0
      let cycleOpenDate = null, lastCycleDate = null, closedBuy = 0, closedSell = 0
      sorted.forEach((t) => {
        const qty = Number(t.quantity) || 0
        const isBuy = String(t.transactionType).toUpperCase() === 'BUY'
        if (netQty === 0) cycleOpenDate = getTradeDateTime(t)
        if (isBuy) { netQty += qty; curBuyQty += qty; curBuyAmt += (Number(t.price) || 0) * qty }
        else { netQty -= qty; curSellQty += qty; curSellAmt += (Number(t.price) || 0) * qty }
        if (netQty === 0) {
          lastCycleDate = cycleOpenDate; cycleOpenDate = null
          closedBuy += curBuyAmt; closedSell += curSellAmt
          curBuyQty = 0; curSellQty = 0; curBuyAmt = 0; curSellAmt = 0
        }
      })
      const row = {
        symbol, netQuantity: netQty,
        totalBuyAmount: netQty !== 0 ? curBuyAmt : closedBuy,
        totalSellAmount: netQty !== 0 ? curSellAmt : closedSell,
        date: netQty !== 0 ? cycleOpenDate : lastCycleDate,
      }
      if (netQty !== 0) active.push(row); else squared.push(row)
    })
    active.sort((a, b) => (b.date || 0) - (a.date || 0))
    squared.sort((a, b) => (b.date || 0) - (a.date || 0))
    return { active, squared }
  }, [uploadedTrades])

  const normalizeSymbol = (s) => { const i = String(s || '').indexOf('-'); return i > 0 ? s.substring(0, i) : s }
  const holdingsPriceMap = React.useMemo(() => {
    const m = {}
    data.holdings.forEach((h) => {
      const sym = normalizeSymbol(h.tradingsymbol)
      // WS tick LTP takes priority over stale API price
      m[sym] = displayTicks?.[sym]?.ltp || displayTicks?.[h.tradingsymbol]?.ltp || Number(h.last_price) || 0
    })
    return m
  }, [data.holdings, displayTicks])
  const monthsSince = (d) => { if (!d) return 0; return Math.max(0, Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30) }

  // ── Table column definitions ────────────────────────────────────────────────
  const uploadedTradesColumns = [
    { key: 'tradeDate', header: 'Date', render: (t) => <div className="text-sm"><div>{new Date(t.tradeDate).toLocaleDateString()}</div><div className="text-gray-500">{t.tradeTime}</div></div> },
    { key: 'tradingsymbol', header: 'Symbol', render: (t) => <div className="flex items-center space-x-2"><span className="font-medium">{t.tradingsymbol}</span><button onClick={() => openTradingView(t.tradingsymbol, t.exchange)} className="text-blue-600 hover:text-blue-800"><ExternalLink className="w-4 h-4" /></button></div> },
    { key: 'transactionType', header: 'Type', render: (t) => <span className={`badge ${t.transactionType === 'BUY' ? 'badge-success' : 'badge-danger'}`}>{t.transactionType}</span> },
    { key: 'quantity', header: 'Qty', accessor: 'quantity' },
    { key: 'price', header: 'Price', render: (t) => `₹${t.price.toFixed(2)}` },
    { key: 'amount', header: 'Amount', render: (t) => `₹${t.amount.toLocaleString('en-IN')}` },
    { key: 'exchange', header: 'Exchange', accessor: 'exchange' },
    { key: 'product', header: 'Product', accessor: 'product' },
  ]
  const activeColumns = [
    { key: 'date', header: 'Date', accessor: (r) => r.date ? r.date.getTime() : 0, render: (r) => <div className="text-sm">{r.date ? r.date.toLocaleDateString() : '-'}</div> },
    { key: 'symbol', header: 'Symbol', accessor: (r) => r.symbol, render: (r) => <div className="flex items-center space-x-2"><span className="font-medium">{r.symbol}</span><button onClick={() => openTradingView(r.symbol, 'NSE')} className="text-blue-600"><ExternalLink className="w-4 h-4" /></button></div> },
    { key: 'netQuantity', header: 'Net Qty', accessor: (r) => r.netQuantity, render: (r) => <span className={r.netQuantity >= 0 ? 'text-success-600' : 'text-danger-600'}>{r.netQuantity}</span> },
    { key: 'expected1', header: 'Expected 1%', accessor: (r) => { const cb = Math.abs((r.totalBuyAmount||0)-(r.totalSellAmount||0)); return cb * Math.pow(1.01, monthsSince(r.date)) }, render: (r) => { const cb = Math.abs((r.totalBuyAmount||0)-(r.totalSellAmount||0)); return `₹${(cb*Math.pow(1.01,monthsSince(r.date))).toLocaleString('en-IN')}` } },
    { key: 'pl1pct', header: 'PL 1%', accessor: (r) => { const ltp = holdingsPriceMap[normalizeSymbol(r.symbol)]||0; const cv = Math.abs(r.netQuantity||0)*ltp; const cb = Math.abs((r.totalBuyAmount||0)-(r.totalSellAmount||0)); const exp = cb*Math.pow(1.01,monthsSince(r.date)); return exp>0?((cv-exp)/exp)*100:0 }, render: (r) => { const ltp = holdingsPriceMap[normalizeSymbol(r.symbol)]||0; const cv = Math.abs(r.netQuantity||0)*ltp; const cb = Math.abs((r.totalBuyAmount||0)-(r.totalSellAmount||0)); const exp = cb*Math.pow(1.01,monthsSince(r.date)); const pct = exp>0?((cv-exp)/exp)*100:0; return <span className={pct>=0?'text-success-600':'text-danger-600'}>{pct>=0?'+':''}{pct.toFixed(2)}%</span> } },
    { key: 'avgPrice', header: 'Avg Price', accessor: (r) => { const n=r.netQuantity||0; return n===0?0:Math.abs(((r.totalBuyAmount||0)-(r.totalSellAmount||0))/n) }, render: (r) => { const n=r.netQuantity||0; const avg=n===0?0:Math.abs(((r.totalBuyAmount||0)-(r.totalSellAmount||0))/n); return `₹${avg.toFixed(2)}` } },
    { key: 'heldAmount', header: 'Held Amount', accessor: (r) => { const n=r.netQuantity||0; const avg=n===0?0:((r.totalBuyAmount||0)-(r.totalSellAmount||0))/n; return Math.abs(n*avg) }, render: (r) => { const n=r.netQuantity||0; const avg=n===0?0:((r.totalBuyAmount||0)-(r.totalSellAmount||0))/n; return `₹${Math.abs(n*avg).toLocaleString('en-IN')}` } },
  ]
  const squaredColumns = [
    { key: 'date', header: 'Date', accessor: (r) => r.date?r.date.getTime():0, render: (r) => <div className="text-sm">{r.date?r.date.toLocaleDateString():'-'}</div> },
    { key: 'symbol', header: 'Symbol', accessor: (r) => r.symbol, render: (r) => <div className="flex items-center space-x-2"><span className="font-medium">{r.symbol}</span><button onClick={()=>openTradingView(r.symbol,'NSE')} className="text-blue-600"><ExternalLink className="w-4 h-4"/></button></div> },
    { key: 'buyAmount', header: 'Buy Amount', accessor: (r) => Number(r.totalBuyAmount||0), render: (r) => `₹${Number(r.totalBuyAmount||0).toLocaleString('en-IN')}` },
    { key: 'sellAmount', header: 'Sell Amount', accessor: (r) => Number(r.totalSellAmount||0), render: (r) => `₹${Number(r.totalSellAmount||0).toLocaleString('en-IN')}` },
    { key: 'pnl', header: 'P&L', accessor: (r) => Number(r.totalSellAmount||0)-Number(r.totalBuyAmount||0), render: (r) => { const pnl=Number(r.totalSellAmount||0)-Number(r.totalBuyAmount||0); return <span className={pnl>=0?'text-success-600':'text-danger-600'}>{pnl>=0?'+':''}₹{pnl.toLocaleString('en-IN')}</span> } },
  ]

  if (loading) {
    return <div className="flex items-center justify-center py-12"><LoadingSpinner size="large" /></div>
  }

  // Whether live Kite holdings actually loaded (kiteConnected may be true but token expired)
  const kiteIsLive = data.holdings.length > 0

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Offline banner — shown when Kite not connected or token expired */}
      {!kiteIsLive && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              {!user?.kiteConnected
                ? 'Kite not connected — showing last saved snapshot.'
                : 'Kite token may have expired — showing last saved snapshot.'}
              {kiteDbFallback.length === 0 && !user?.kiteConnected && ' No snapshot yet; connect Kite once to populate it.'}
            </span>
          </div>
          <a href="/settings" className="text-amber-700 font-medium underline whitespace-nowrap">
            Settings →
          </a>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input type="checkbox" checked={autoRefreshEnabled} onChange={(e) => setAutoRefreshEnabled(e.target.checked)} />
            <span>Auto</span>
          </label>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-600">Every</span>
            <input type="number" min="5" value={autoRefreshSec} onChange={(e) => setAutoRefreshSec(Math.max(5, parseInt(e.target.value, 10) || 60))} className="input w-20" />
            <span className="text-gray-600">sec</span>
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className="btn-secondary flex items-center space-x-2">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <PortfolioSummaryBar
        accounts={summaryAccounts}
        mfTotal={mfSummary.totalValue || 0}
        mfSummary={mfSummary}
        uniqueEquityCount={uniqueEquityCount}
      />

      {/* Quick stats — dynamic per active tab */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card py-4">
          <p className="text-xs font-medium text-gray-500 mb-1">{activeTabSummary.label} Total Value</p>
          <p className="text-xl font-bold text-gray-900">
            ₹{(activeTabSummary.totalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{activeTabSummary.holdingsCount || 0} holdings</p>
        </div>
        <div className="card py-4">
          <p className="text-xs font-medium text-gray-500 mb-1">{activeTabSummary.label} P&amp;L</p>
          <p className={`text-xl font-bold ${(activeTabSummary.totalPnl || 0) >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
            {(activeTabSummary.totalPnl || 0) >= 0 ? '+' : ''}
            ₹{(activeTabSummary.totalPnl || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <p className={`text-xs mt-0.5 ${(activeTabSummary.totalPnl || 0) >= 0 ? 'text-success-500' : 'text-danger-500'}`}>
            {(activeTabSummary.totalPnlPct || 0) >= 0 ? '+' : ''}{(activeTabSummary.totalPnlPct || 0).toFixed(2)}%
          </p>
        </div>
      </div>

      {/* ── Holdings Section with Tabs ── */}
      <div className="card p-0 overflow-hidden">
        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50">
          {HOLDINGS_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setHoldingsTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                holdingsTab === tab.id
                  ? 'border-primary-600 text-primary-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.live && (
                <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-success-400 align-middle" title="Live" />
              )}
            </button>
          ))}
        </div>

        <div className="p-4">

          {/* ── KitePM — live + DB fallback + supplemental ── */}
          {holdingsTab === 'kite_pm' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {kiteLiveHoldings.length > 0
                    ? <>{kiteLiveHoldings.length} live · {kitePMSupplemental.length} manual</>
                    : kiteDbFallback.length > 0
                      ? <><span className="text-amber-600 font-medium">{kiteDbFallback.length} from last sync</span> · {kitePMSupplemental.length} manual · <span className="text-gray-300">Kite offline — showing saved data</span></>
                      : <span className="text-gray-300">No holdings yet — connect Kite or add manually</span>
                  }
                </p>
                <button
                  onClick={() => openAddHolding('kite_pm')}
                  className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Holding
                </button>
              </div>
              <HoldingsTable
                holdings={kitePMAllHoldings}
                isLive={true}
                holdingAges={holdingAges}
                onBuyLive={(h) => h._source === 'live' && openOrderModal(h._raw || h, 'BUY', h.ltp)}
                onSellLive={(h) => h._source === 'live' && openOrderModal(h._raw || h, 'SELL', h.ltp)}
                onEdit={(h, idx) => openEditHolding('kite_pm', h, idx)}
                onDelete={(idx) => deleteHolding('kite_pm', idx)}
              />
            </div>
          )}

          {/* ── External accounts (Kite RJ, AngelOne RJ, AngelOne PM) ── */}
          {['kite_rj', 'angelone_rj', 'angelone_pm'].includes(holdingsTab) && (() => {
            const ext = externalHoldings[holdingsTab]
            const externalRows = (ext?.normalizedHoldings || []).map((h, i) => ({
              ...h,
              _editable: true,
              _manualIndex: i,
            }))
            return (
              <div className="space-y-4">
                {/* Add Holding — above table */}
                <div className="flex justify-end">
                  <button
                    onClick={() => openAddHolding(holdingsTab)}
                    className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Holding
                  </button>
                </div>

                {/* Holdings table */}
                <HoldingsTable
                  holdings={externalRows}
                  isLive={false}
                  brokerUrl={ext?.brokerUrl}
                  holdingAges={holdingAges}
                  onEdit={(h, idx) => openEditHolding(holdingsTab, h, idx)}
                  onDelete={(idx) => deleteHolding(holdingsTab, idx)}
                />

                {/* JSON upload — below table */}
                <div className="border-t border-gray-100 pt-4">
                  <JsonUploadPanel
                    accountName={holdingsTab}
                    uploadedAt={ext?.uploadedAt}
                    onUploaded={(d) => setExternalHoldings((prev) => ({ ...prev, [holdingsTab]: d }))}
                    onCleared={() => setExternalHoldings((prev) => ({ ...prev, [holdingsTab]: null }))}
                  />
                </div>
              </div>
            )
          })()}

          {/* ── Mutual Funds — dedicated MFTab component ── */}
          {holdingsTab === 'mf' && (
            <MFTab onSummaryChange={setMfSummary} />
          )}
        </div>
      </div>

      {/* ── Upload Tradebook CSV ── */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Upload className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Upload Tradebook CSV</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => { setSelectedFile(e.target.files[0]); setUploadSummary(null) }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            <button onClick={handleUploadCsv} disabled={!selectedFile || uploading} className="btn-primary flex items-center space-x-2">
              {uploading ? <LoadingSpinner size="small" /> : <Upload className="w-4 h-4" />}
              <span>Upload</span>
            </button>
          </div>
          {uploadSummary && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Upload Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-blue-700">Processed:</span><span className="font-medium ml-1">{uploadSummary.processed}</span></div>
                <div><span className="text-blue-700">Saved:</span><span className="font-medium ml-1 text-success-600">{uploadSummary.saved}</span></div>
                <div><span className="text-blue-700">Duplicates:</span><span className="font-medium ml-1 text-yellow-600">{uploadSummary.duplicates}</span></div>
                <div><span className="text-blue-700">Errors:</span><span className="font-medium ml-1 text-danger-600">{uploadSummary.errors}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Trade History ── */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <FileText className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Trade History</h3>
        </div>
        <div className="mb-4 flex items-center space-x-2">
          {['ALL', 'ACTIVE', 'SQUARED'].map((v) => (
            <button key={v} onClick={() => setTradeView(v)} className={`btn-secondary ${tradeView === v ? 'ring-2 ring-primary-300' : ''}`}>
              {v === 'SQUARED' ? 'Squared Off' : v.charAt(0) + v.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        {uploadedTrades.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No trade history found</p>
            <p className="text-sm">Upload a CSV to see historical trades</p>
          </div>
        ) : tradeView === 'ALL' ? (
          <SortableTable data={uploadedTrades} columns={uploadedTradesColumns} searchPlaceholder="Search trade history..." defaultSortKey="tradeDate" defaultSortDirection="desc" />
        ) : tradeView === 'ACTIVE' ? (
          <SortableTable data={groupedTrades.active} columns={activeColumns} searchPlaceholder="Search active..." defaultSortKey="date" defaultSortDirection="desc" />
        ) : (
          <SortableTable data={groupedTrades.squared} columns={squaredColumns} searchPlaceholder="Search squared off..." defaultSortKey="date" defaultSortDirection="desc" />
        )}
      </div>

      {/* ── Automated CNC Sell (password-gated) ── */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Automated CNC Sell</h3>
            {automationUnlocked ? (
              <span className="text-xs bg-success-100 text-success-700 px-2 py-0.5 rounded-full font-medium">Unlocked</span>
            ) : (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
          </div>
          {!automationUnlocked && (
            <button
              onClick={() => setPasswordModal(true)}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Lock className="w-4 h-4" />
              Unlock Automation
            </button>
          )}
        </div>

        {automationUnlocked ? (
          <div className="p-0">
            <AutomationPanel holdings={data.holdings} />
          </div>
        ) : (
          <div className="px-6 py-10 text-center text-gray-400">
            <Lock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">Password required to access automation controls</p>
            <p className="text-xs mt-1">This prevents accidental order execution</p>
            <button onClick={() => setPasswordModal(true)} className="mt-4 btn-primary text-sm">
              Enter Password to Unlock
            </button>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <PasswordConfirmModal
        isOpen={passwordModal}
        title="Unlock Automation"
        description="Enter your account password to enable the Automated CNC Sell system for this session."
        onConfirm={() => {
          setAutomationUnlocked(true)
          setPasswordModal(false)
          toast.success('Automation unlocked for this session')
        }}
        onCancel={() => setPasswordModal(false)}
      />

      <HoldingFormModal
        isOpen={holdingFormModal.isOpen}
        initialData={holdingFormModal.initialData}
        accountLabel={HOLDINGS_TABS.find((t) => t.id === holdingFormModal.accountName)?.label || ''}
        onClose={() => setHoldingFormModal({ isOpen: false, editIndex: null, initialData: null, accountName: null })}
        onSave={saveHolding}
      />

      <OrderModal
        isOpen={orderModal.isOpen}
        onClose={closeOrderModal}
        stock={orderModal.stock}
        orderType={orderModal.orderType}
        ltp={orderModal.ltp}
        existingHoldings={data.holdings}
      />
    </div>
  )
}

export default Portfolio
